import { useState } from 'react';
import { AlertCircle, ChevronDown, ChevronRight, HelpCircle, Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { useEffectiveCpv } from '../hooks/use-dossier-company';
import type { EffectiveCpvResult, NafCpvFallback } from '../types/dossier-company.types';

/**
 * Sprint 5b.1 v2 — Phase C.3 : affichage Bloc 1 (périmètre auto NAF) et Bloc 4
 * (récapitulatif effectif) de la section Stratégie commerciale.
 *
 * Pas de checkbox : le périmètre auto NAF est une donnée système, pas une saisie
 * à valider. L'utilisateur intervient via les Blocs 2 (extensions) et 3 (exclusions).
 */
export function EffectiveCpvDisplay() {
  const { data, isLoading } = useEffectiveCpv();

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-4">
      <AutoNafBlock data={data} />
      <EffectiveSummaryBlock data={data} />
    </div>
  );
}

function AutoNafBlock({ data }: { data: EffectiveCpvResult }) {
  const { autoFromNaf } = data;
  const [mediumOpen, setMediumOpen] = useState(false);

  if (autoFromNaf.fallback) {
    return <FallbackMessage fallback={autoFromNaf.fallback} nafCode={autoFromNaf.nafCode} />;
  }

  return (
    <div className="rounded-md border bg-muted/30 p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="text-sm">
          <span className="text-muted-foreground">Votre périmètre métier est dérivé de votre code </span>
          <span className="font-mono font-semibold">
            {autoFromNaf.nomenclatureUsed === 'NAFRev2_via_2025_transposition'
              ? `NAF 2025 ${autoFromNaf.nafCode}`
              : `NAF Rev. 2 ${autoFromNaf.nafCode}`}
          </span>
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            >
              <HelpCircle className="h-3.5 w-3.5" />
              Pourquoi ces CPV ?
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-80 text-xs">
            <p>
              Ce périmètre est calculé automatiquement depuis votre code NAF déclaré à l'INSEE.
              Le mapping NAF→CPV est LLM-asserted et peut être imprécis.
            </p>
            <p className="mt-2">
              Vous pouvez compenser via les <strong>Extensions</strong> (codes à ajouter) ou
              les <strong>Exclusions</strong> (codes à retirer) ci-dessous.
            </p>
          </PopoverContent>
        </Popover>
      </div>

      {/* Pertinence haute */}
      <div className="mb-3">
        <div className="flex items-center gap-2 mb-2">
          <Badge variant="default" className="bg-emerald-600 hover:bg-emerald-600">
            Pertinence haute
          </Badge>
          <span className="text-xs text-muted-foreground">
            {autoFromNaf.high.length} code{autoFromNaf.high.length > 1 ? 's' : ''}
          </span>
        </div>
        {autoFromNaf.high.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">Aucun code en pertinence haute.</p>
        ) : (
          <ul className="space-y-1 text-xs">
            {autoFromNaf.high.map((m) => (
              <li key={m.cpvCode} className="flex items-start gap-2">
                <span className="font-mono text-muted-foreground shrink-0">{m.cpvCode}</span>
                <span className="flex-1">{m.labelCpv}</span>
                {m.sectorCpv && (
                  <span className="text-muted-foreground italic shrink-0 max-w-[40%] truncate">
                    {m.sectorCpv}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pertinence modérée */}
      {autoFromNaf.medium.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setMediumOpen((v) => !v)}
            className="flex items-center gap-2 mb-2 hover:opacity-80"
          >
            {mediumOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            <Badge variant="secondary">Pertinence modérée</Badge>
            <span className="text-xs text-muted-foreground">
              {autoFromNaf.medium.length} code{autoFromNaf.medium.length > 1 ? 's' : ''}
            </span>
          </button>
          {mediumOpen && (
            <ul className="space-y-1 text-xs">
              {autoFromNaf.medium.map((m) => (
                <li key={m.cpvCode} className="flex items-start gap-2">
                  <span className="font-mono text-muted-foreground shrink-0">{m.cpvCode}</span>
                  <span className="flex-1">{m.labelCpv}</span>
                  {m.sectorCpv && (
                    <span className="text-muted-foreground italic shrink-0 max-w-[40%] truncate">
                      {m.sectorCpv}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function FallbackMessage({
  fallback,
  nafCode,
}: {
  fallback: NafCpvFallback;
  nafCode: string | null;
}) {
  const messages: Record<NafCpvFallback, { title: string; description: string }> = {
    NO_NAF_CODE: {
      title: 'Code NAF manquant',
      description:
        'Aucun code NAF n\'est rattaché à votre company. Lancez un refresh SIRENE depuis la section Identité pour récupérer votre code APE/NAF.',
    },
    NO_TRANSPOSITION_AVAILABLE: {
      title: 'Transposition NAF 2025 indisponible',
      description: `Votre company utilise la nomenclature NAF 2025 (${nafCode ?? 'code inconnu'}), mais la table de transposition vers NAF Rev. 2 n'a pas encore été chargée côté serveur. Le périmètre automatique n'est pas calculable pour l'instant. Vous pouvez tout de même définir manuellement vos extensions.`,
    },
    NAF_CODE_NOT_IN_REFERENTIAL: {
      title: 'Code NAF non couvert',
      description: `Votre code NAF ${nafCode ?? ''} n'a pas de mapping CPV connu (~99% des codes APE sont couverts). Vous pouvez définir manuellement vos extensions.`,
    },
  };

  const { title, description } = messages[fallback];

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/40 dark:bg-amber-950/30">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold mb-1">{title}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
    </div>
  );
}

function EffectiveSummaryBlock({ data }: { data: EffectiveCpvResult }) {
  const [detailOpen, setDetailOpen] = useState(false);
  const fromHighSet = new Set(data.autoFromNaf.high.map((m) => m.cpvCode));
  const fromMediumSet = new Set(data.autoFromNaf.medium.map((m) => m.cpvCode));
  const extensionSet = new Set(data.targetExtension);

  const decorated = data.effective.map((code) => {
    let origin: 'high' | 'medium' | 'extension' = 'extension';
    if (fromHighSet.has(code)) origin = 'high';
    else if (fromMediumSet.has(code)) origin = 'medium';
    else if (extensionSet.has(code)) origin = 'extension';
    return { code, origin };
  });

  return (
    <div className="rounded-md border bg-card p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Info className="h-4 w-4 text-muted-foreground" />
          <p className="text-sm">
            <span className="font-semibold">{data.effectiveCount}</span>{' '}
            code{data.effectiveCount > 1 ? 's' : ''} CPV dans votre périmètre commercial effectif
          </p>
        </div>
        {data.effectiveCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDetailOpen(true)}
            className="text-xs"
          >
            Voir le détail
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        Ce périmètre alimente le sourcing automatique d'AO, la checklist GO catégorie
        sectorielle, et le scoring fit.
      </p>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Périmètre effectif — {data.effectiveCount} code{data.effectiveCount > 1 ? 's' : ''}
            </DialogTitle>
          </DialogHeader>
          <ul className="space-y-1 text-sm mt-2">
            {decorated.map(({ code, origin }) => (
              <li key={code} className="flex items-center gap-2 py-1 border-b last:border-0">
                <span className="font-mono text-xs text-muted-foreground w-24 shrink-0">
                  {code}
                </span>
                <OriginBadge origin={origin} />
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OriginBadge({ origin }: { origin: 'high' | 'medium' | 'extension' }) {
  if (origin === 'high')
    return <Badge className="bg-emerald-600 hover:bg-emerald-600">via NAF haute</Badge>;
  if (origin === 'medium') return <Badge variant="secondary">via NAF modérée</Badge>;
  return <Badge variant="outline">extension manuelle</Badge>;
}
